import type { ReactNode } from 'react';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { isNonEmptyString } from '@wistia/type-guards';
import { AriaLiveContext } from './AriaLiveContext';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';

const debugStyle = {
  position: 'absolute',
  border: '3px dashed red',
  backgroundColor: 'white',
  padding: '10px',
  margin: '5px',
  minHeight: '50px',
  width: 'calc(100% - 10px)',
};

type AriaLiveProviderProps = {
  /**
   * Pass an arbitrary child node
   */
  children: ReactNode;
  /**
   * @ignore
   * Set to true to enable debug mode
   */
  debug?: boolean;
};

export const AriaLiveProvider = ({ children, debug = false }: AriaLiveProviderProps) => {
  const [message, setMessage] = useState<string | null>(null);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // create the live region DOM element
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('role', 'alert');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');

    // apply visually hidden (or debug) styles
    Object.assign(liveRegion.style, debug ? debugStyle : visuallyHiddenStyle);

    document.body.appendChild(liveRegion);
    liveRegionRef.current = liveRegion;

    return () => {
      // remove live region on unmount
      if (liveRegionRef.current) {
        document.body.removeChild(liveRegionRef.current);
      }
    };
  }, [debug]);

  useEffect(() => {
    if (liveRegionRef.current && isNonEmptyString(message)) {
      liveRegionRef.current.textContent = message;
    }
  }, [message]);

  // avoid unnecessary re-renders
  const announce = useCallback(
    (announcement: string, politeness: 'assertive' | 'polite' = 'polite') => {
      if (liveRegionRef.current) {
        liveRegionRef.current.setAttribute('aria-live', politeness);
        setMessage(announcement);

        // clear message after a short interval to ensure new announcements can be read
        setTimeout(() => setMessage(null), 1000);
      }
    },
    [],
  );

  // ensure the value passed to the Provider doesn't change on every render
  const value = useMemo(() => ({ announce }), [announce]);

  return <AriaLiveContext.Provider value={value}>{children}</AriaLiveContext.Provider>;
};
